import React, { useEffect, useState, useCallback } from 'react';
import SupportChat from '../components/chat/SupportChat';
import { createRoom, handleJoinRoom, updateRoomList } from '../features/chatkasten/chat';
import { MenuArea } from '../components/MenuArea';
import { RoundButton } from '../components/buttons/base/RoundButton';
import { isResponsiveView } from '../features/responsive';
import { EditorNavMenu } from '../components/EditorNavMenu';
import { hideModal, QuestionModal, showQuestionModal } from '../components/modals/QuestionModal';
import { TaskArea } from '../components/taskarea/TaskArea';
import { getDomainCfg, store } from '../core/state';
import { ProjectNameLabel } from '../components/ProjectNameLabel';
import { LoginRegisterModal } from '../components/modals/LoginRegisterModal';
import { MainMenu } from '../components/MainMenu';
import { CHAT_VIEW_NAME, EDITOR_VIEW_NAME, THEME } from '../core/constants';
import { authenticateUser, executeProjectNameTask } from '../features/chatrequests';
import SupportChatWebSocket from '../components/chat/SupportChatWebSocket';
import { EditProjectData } from '../components/EditProjectData';
import { getTemplateFields, setProjectFieldValueByKey, setTemplateFields } from '../features/templates';
import { saveUserData } from '../core/code/saver';
import { translate } from '../core/i18n';
import { refreshPage } from '../features/utils';
import { getNewTemplateProjectId } from '../core/project/projectslist';
import { getRedirectUrl, redirectTo } from '../features/redirect';
import { getAppCfg } from '../core/app-cfg';
import { HoverButton } from '../components/buttons/base/HoverButton';
import { useAuth } from '../hooks/useAuth';
import { getCurrentTheme } from '../components/ThemeContext';
import { getCustomMenuItem } from '../features/custommenuitem';

export const ChatView = () => {
  const { isAuthenticated, authenticate } = useAuth();
  // Zustände für die verschiedenen Teile der Komponente
  const [resetMessagesTrigger, setResetMessagesTrigger] = useState(false);
  const [chatState] = store.useState(CHAT_VIEW_NAME); // Zustand des Chats
  const [editorState] = store.useState(EDITOR_VIEW_NAME); // Zustand des Editors
  const [rooms, setRooms] = useState([]);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [showRoomList, setShowRoomList] = useState(true);
  const [projectNameUpdated, setProjectNameUpdated] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentTheme, setCurrentTheme] = store.useState(THEME);

  useEffect(() => {
    if (isAuthenticated) {
      console.log("User authenticated in ChatView");
      // You can perform any actions that depend on authentication here
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (Object.keys(currentTheme).length === 0) {
      const theme = getCurrentTheme();
      setCurrentTheme(theme);      
    }
  }, [currentTheme]);

  useEffect(() => {
    handleProjectNameTask();
  }, [editorState]);

  const handleProjectNameTask = async () => {
    const chatMessages = editorState.data?.chatMessages;
    const hasOriginalProjectName = (editorState.project?.projectOrigin === undefined || (editorState.project?.projectOrigin && editorState.project?.projectOrigin === editorState.project?.project));    
    if (!projectNameUpdated && hasOriginalProjectName && chatMessages && chatMessages.length > 2) {
      const newProjectName = await executeProjectNameTask(chatMessages);
      if (newProjectName.status === 'ok') {
        setProjectNameUpdated(true);
        const projectTemplateFields = getTemplateFields();
        const newFields = setProjectFieldValueByKey(projectTemplateFields, '$Chat-Thema', newProjectName.result); 
        setTemplateFields(newFields);
        saveUserData();        
      }
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };  

  const resetProjectName = async () => {
    const hasOriginalProjectName = (editorState.project?.projectOrigin === undefined || (editorState.project?.projectOrigin && editorState.project?.projectOrigin === editorState.project?.project));    
    if (projectNameUpdated || !hasOriginalProjectName) {
      const projectTemplateFields = getTemplateFields();
      const newFields = setProjectFieldValueByKey(projectTemplateFields, '$Chat-Thema', editorState.project?.projectOrigin); 
      setTemplateFields(newFields);
      saveUserData();
      setProjectNameUpdated(false)
    }
  }

  const handleCreateRoom = async () => {
    const appCfg = getAppCfg();
    let currentProjectId = appCfg.projectId;

    const code = currentProjectId.split('_')[0];
    const newProjectId = await getNewTemplateProjectId(code);    

    redirectTo(getRedirectUrl(getDomainCfg().viewSettings.editorView, newProjectId));    
  };

  const resetRequested = () => {
    showQuestionModal(
      translate("chat-reset-code-modal-title"),
      translate("chat-reset-code-modal-message"),
      resetUserCodeRequested,
      cancelResetCode
    );   
    
  };
  const resetUserCodeRequested = async () => {
    setResetMessagesTrigger(prev => !prev);
    await resetProjectName();
    //TODO reset, task execution, too!?
    hideModal();
  }

  const cancelResetCode = () : void => {
    hideModal();
  }

  const joinRoomRequested = async (roomId) => {
    const newRoomId = await handleJoinRoom(roomId);
    if (newRoomId) {
      setCurrentRoomId(newRoomId);
    }
  }

  const toggleRoomList = () => {
    // Funktion, um die Raumliste ein-/auszublenden
    setShowRoomList(!showRoomList);
  }

  const hasRoomList = chatState.cfg?.hasThreadList;
  const newChatButton = <RoundButton title="Neuer Chat" text="Neuer Chat" onClick={handleCreateRoom} icon="plus" iconColor="#ffffff" borderColor='#2243ba' backgroundColor='#305eff' />;
  const resetChatButton = <RoundButton className="reset-btn btn-margins" icon="trash-can" onClick={resetRequested} title={translate('Chat leeren')} />;

  const hasHelpContent = ( editorState.project?.hint || editorState?.project?.subTasks?.length > 0);
  const showHelpTaskArea = editorState.cfg?.editorFeatures?.helpActive && hasHelpContent;
  const hasTaskContent = (editorState.project?.task || editorState.project?.subtasks || editorState.project?.steps) || showHelpTaskArea;
  const taskAreaEnabled = editorState.cfg?.editorFeatures?.showTaskButton && hasTaskContent;
  const taskArea = taskAreaEnabled && <TaskArea />;  

  const logoElm = <a href={getDomainCfg().product.homeUrl} className="brand-logo"><img src={getDomainCfg().product.logoUrl} alt='Logo'/></a>;

  const menuLeft = !isSidebarCollapsed && showRoomList && (
    <MenuArea bg={currentTheme}>
        { logoElm }
        { hasRoomList && newChatButton }   
        <HoverButton title="SideBar ausblenden" className="sidebar-btn" onClick={toggleSidebar} icon="chevron-left" />     
    </MenuArea>
  );

  const roomList = showRoomList && (
    <div id="rooms" className="list-group" style={{ height: '80%', overflowY: 'scroll', border: '1px solid #ddd', minHeight:'300px' }}>
      {rooms.map(room => (
        <button
          type="button"
          key={room.roomId}
          className="list-group-item list-group-item-action"
          onClick={async () => await joinRoomRequested(room.roomId)}
        >
          {room.name}
        </button>
      ))}
    </div>
  );

  const roomListToggle = <RoundButton title="toggle" onClick={toggleRoomList} icon="comments" iconColor="#ffffff" borderColor='#2243ba' backgroundColor='#305eff' />;

  const handleReAuthenticate = useCallback(async () => {
    console.log('Attempting to re-authenticate');
    const success = await authenticate();
    if (success) {
      console.log('Re-authentication successful');
    } else {
      console.log('Re-authentication failed');
    }
    return success;
  }, [authenticate]);

  const responsiveView = isResponsiveView();  
  let menuRight = null;
  if (!responsiveView) {
    menuRight = <MenuArea bg={currentTheme}>
      {(() => {
      let navBarItems = chatState.cfg?.navbarright?.reduce((groups, item, index) => {
        const groupId = item.groupId || 'default';
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        
        let component = null;
        if (item.type === "component") {
          // Existing logic components
          if (item.name === "sidebarToggle" && isSidebarCollapsed) {
            component = <HoverButton title="SideBar einblenden" className="sidebar-btn mr-5" onClick={toggleSidebar} icon="chevron-right" key={index} />;
          }
          else if (item.name === "roomList" && hasRoomList) {
            component = roomListToggle;
          }
          else if (item.name === "newChat" && (!showRoomList || !hasRoomList)) {
            component = newChatButton;
          }
          // Standard components
          else if (item.name === "editornavmenu") {
            component = <EditorNavMenu key={index} layout={editorState?.cfg?.editorFeatures?.layout} />;
          }
          else if (item.name === "projectnamelabel") {
            component = <ProjectNameLabel project={editorState?.project} key={index} />;
          }
          else if (item.name === "mainmenu") {
            component = <MainMenu withProject={true} menuitems={chatState?.cfg?.mainmenu} burger={true} key={index} />;
          }
          else if (item.name === "reset") {
            component = resetChatButton;
          }
        } else if (item.type === "link" || item.type === "button") {
          component = getCustomMenuItem(item, index);
        } else if (item.type === 'brand' && (!hasTaskContent || isSidebarCollapsed) && !hasRoomList) {
          component = logoElm;
        }
        
        if (component) {
          groups[groupId].push(component);
        }
        
        return groups;
      }, {}) || {};

      return (
        <div style={{display: 'flex', alignItems: 'center', width: '100%'}}>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            {navBarItems.start || []}
          </div>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center', flex: 1, justifyContent: 'center'}}>
            {navBarItems.center || []}
          </div>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            {navBarItems.right || []}
          </div>
        </div>
      );
    })()}
    </MenuArea>;
  } else {
    menuRight = <MenuArea bg={currentTheme}>
      {(() => {
      let navBarItems = chatState.cfg?.navbarright?.reduce((groups, item, index) => {
        const groupId = item.groupId || 'default';
        if (!groups[groupId]) {
          groups[groupId] = [];
        }
        
        let component = null;
        if (item.type === "component") {
          if (item.name === "newChat" && (!showRoomList || !hasRoomList)) {
            component = newChatButton;
          }
          else if (item.name === "mainmenu") {
            component = <MainMenu withProject={true} menuitems={chatState?.cfg?.mainmenu} burger={true} key={index} />;
          }
        } else if (item.type === 'brand' && (!hasTaskContent || isSidebarCollapsed) && !hasRoomList) {
          component = logoElm;
        }
        
        if (component) {
          groups[groupId].push(component);
        }
        
        return groups;
      }, {}) || {};

      return (
        <div style={{display: 'flex', alignItems: 'center', width: '100%'}}>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            {navBarItems.start || []}
          </div>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center', flex: 1, justifyContent: 'center'}}>
            {navBarItems.center || []}
          </div>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            {navBarItems.right || []}
          </div>
        </div>
      );
    })()}
    </MenuArea>;
  }


  const showEditData = editorState?.data?.editDataVisible;
  const editProjectDataView = <EditProjectData />;

  const fullWidth = !isSidebarCollapsed && showRoomList && (hasRoomList || taskAreaEnabled);

  const USE_WEBSOCKETS = getDomainCfg().chatSettings.useWebsocket;
  const chat = USE_WEBSOCKETS ? (
    <SupportChatWebSocket 
      hasProjectContext={false} 
      resetMessages={resetMessagesTrigger} 
      isAuthenticated={isAuthenticated}
      onReAuthenticate={handleReAuthenticate}
    />
  ) : (
    <SupportChat hasProjectContext={false} resetMessages={resetMessagesTrigger} />
  );

  return ( <>
    <LoginRegisterModal />
    <QuestionModal />
    <div className="container-fluid">
      <div className="row" style={{ height: isResponsiveView() ? '100vh' : '100vh' }}>
        { !isSidebarCollapsed && showRoomList && taskAreaEnabled && <div className="col-md-3 chatSideBar">
          { menuLeft }
          { hasRoomList && roomList }
          { taskArea }
        </div>}
        <div className={`col-md-${fullWidth ? '9' : '12'} chatParent`}>
          { menuRight }
          { showEditData && editProjectDataView }
          { !showEditData && chat }
        </div>
      </div>
    </div>
    </>
  );
};
